import React, { useState, useEffect, useRef, useCallback } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import OrderCard from "../components/Orders/orderCard"; // adjust path as needed
import OrderDetails from "../components/Orders/orderDetails";
import "./ordersPanel.scss";
import { extractInvoiceIdFromInput } from "../utils/invoice";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "downloaded", label: "Downloaded" },
  { value: "inprogress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const buildPaginationItems = (currentPage, totalPages, maxVisible = 7) => {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1);
  }

  const pages = [1];
  const middleSlots = maxVisible - 2;
  let start = Math.max(2, currentPage - Math.floor(middleSlots / 2));
  let end = start + middleSlots - 1;

  if (end > totalPages - 1) {
    end = totalPages - 1;
    start = end - middleSlots + 1;
  }

  if (start > 2) pages.push("ellipsis-left");
  for (let pageNum = start; pageNum <= end; pageNum += 1) {
    pages.push(pageNum);
  }
  if (end < totalPages - 1) pages.push("ellipsis-right");
  pages.push(totalPages);
  return pages;
};

function OrdersPanel({ orders, users, fetchOrders, resetSelectedOrder, onVisibleOrdersChange }) {
  const [orderStatusUpdates, setOrderStatusUpdates] = useState({});
  const [userDetails, setUserDetails] = useState({});
  const [previewToggles, setPreviewToggles] = useState({});
  const [tab, setTab] = useState('All');
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQualifier, setSearchQualifier] = useState("asc");
  const [searchBy, setSearchBy] = useState(""); // 'name' | 'orderId' | 'imageName' | 'all'
  const [activeOrder, setActiveOrder] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [tableSort, setTableSort] = useState({ field: 'date', direction: 'desc' });
  const [statusFilters, setStatusFilters] = useState([]);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const statusMenuRef = useRef(null);
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const bulkMenuRef = useRef(null);

  const normalizeStatus = (value) => {
    if (!value) return '';
    return String(value).toLowerCase().replace(/[\s_-]+/g, '');
  };

  const getCustomerName = (order) => {
    const user = userDetails[order.customerId];
    const organization = user?.freshbooksClientId?.organization;
    const name = organization || `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
    return name || '';
  };

  const getCreatedAtMs = (order) => {
    const value = order?.createdAt;
    if (!value) return null;
    if (typeof value?.toDate === 'function') {
      try {
        const date = value.toDate();
        return date instanceof Date && !Number.isNaN(date.getTime()) ? date.getTime() : null;
      } catch (_) {
        return null;
      }
    }
    const dateValue = value instanceof Date ? value : new Date(value);
    return dateValue instanceof Date && !Number.isNaN(dateValue.getTime())
      ? dateValue.getTime()
      : null;
  };

  const getOrderTotalValue = (order) => {
    const candidates = [
      order?.totalCents != null ? order.totalCents / 100 : null,
      order?.amountCents != null ? order.amountCents / 100 : null,
      order?.grandTotalCents != null ? order.grandTotalCents / 100 : null,
      order?.total,
      order?.amount,
      order?.price,
      order?.totalPrice,
      order?.grandTotal,
    ];
    for (const candidate of candidates) {
      const num = Number(candidate);
      if (Number.isFinite(num)) return num;
    }
    const fallback = Number(order?.amountPaid);
    return Number.isFinite(fallback) ? fallback : 0;
  };

  const parseQuantity = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return null;
    return Math.max(1, Math.round(num));
  };

  const getEntryQuantity = (entry) => {
    if (!entry || typeof entry !== 'object') return 1;
    const candidates = [entry.quantity, entry.count, entry.qty];
    for (const candidate of candidates) {
      const parsed = parseQuantity(candidate);
      if (parsed != null) return parsed;
    }
    return 1;
  };

  const sumQuantities = (list) => {
    if (!Array.isArray(list) || !list.length) return null;
    return list.reduce((total, entry) => total + getEntryQuantity(entry), 0);
  };

  const getItemsCount = (order) => {
    const picturesTotal = sumQuantities(order?.pictures);
    if (picturesTotal != null) return picturesTotal;
    const itemsTotal = sumQuantities(order?.items);
    if (itemsTotal != null) return itemsTotal;
    const imagesTotal = sumQuantities(order?.images);
    return imagesTotal != null ? imagesTotal : 0;
  };

  const getPaymentState = (order) => {
    const direct = normalizeStatus(order?.paymentStatus);
    if (['paid', 'sent', 'unsent', 'unpaid'].includes(direct)) {
      return direct;
    }

    const paymentKey = normalizeStatus(order?.paymentTerms || order?.paymentType);
    if (paymentKey === 'stripe') {
      return 'paid';
    }
    if (paymentKey === 'net30') {
      return 'unsent';
    }

    const total = Number(getOrderTotalValue(order));
    const paid = Number(order?.amountPaid ?? 0);
    const paymentSuccess = total > 0
      ? paid >= total
      : Boolean(order?.paidInFull || normalizeStatus(order?.paymentStatus) === 'success');
    return paymentSuccess ? 'paid' : 'unpaid';
  };

  const getSortMeta = (order, field) => {
    switch (field) {
      case 'order':
        return { type: 'string', value: order?.id ? String(order.id) : '' };
      case 'date':
        return { type: 'number', value: getCreatedAtMs(order) };
      case 'customer':
        return { type: 'string', value: getCustomerName(order) };
      case 'total':
        return { type: 'number', value: getOrderTotalValue(order) };
      case 'items':
        return { type: 'number', value: getItemsCount(order) };
      case 'fulfillment':
        return { type: 'string', value: normalizeStatus(order?.status) };
      case 'payment':
        return { type: 'string', value: getPaymentState(order) };
      default:
        return { type: 'string', value: '' };
    }
  };

  const compareOrders = (a, b) => {
    const { field, direction } = tableSort;
    const dir = direction === 'asc' ? 1 : -1;
    const metaA = getSortMeta(a, field);
    const metaB = getSortMeta(b, field);

    if (metaA.type === 'number' || metaB.type === 'number') {
      const fallback = dir === 1 ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
      const numA = Number.isFinite(metaA.value) ? metaA.value : fallback;
      const numB = Number.isFinite(metaB.value) ? metaB.value : fallback;
      if (numA === numB) return 0;
      return numA > numB ? dir : -dir;
    }

    const fallback = dir === 1 ? '\uffff' : '';
    const strA = metaA.value ? String(metaA.value).toLowerCase() : fallback;
    const strB = metaB.value ? String(metaB.value).toLowerCase() : fallback;
    return strA.localeCompare(strB) * dir;
  };

  const handleHeaderSort = (field) => {
    setTableSort((prev) => {
      if (prev.field === field) {
        const nextDirection = prev.direction === 'asc' ? 'desc' : 'asc';
        return { field, direction: nextDirection };
      }
      const baseDirection = field === 'date' ? 'desc' : 'asc';
      return { field, direction: baseDirection };
    });
  };

  const renderSortableHeader = (field, label) => {
    const isActive = tableSort.field === field;
    const direction = isActive ? tableSort.direction : null;
    const icon = direction === 'asc' ? '▲' : direction === 'desc' ? '▼' : '↕';
    const textLabel = typeof label === 'string' ? label : String(label);
    const ariaLabel = isActive
      ? `${textLabel} sorted ${direction === 'asc' ? 'ascending' : 'descending'}`
      : `Sort by ${textLabel}`;
    return (
      <button
        type="button"
        onClick={() => handleHeaderSort(field)}
        className={`op-sortable${isActive ? ' is-active' : ''}`}
        aria-label={ariaLabel}
      >
        <span className="op-sortable__label">{label}</span>
        <span className="op-sortable__icon" aria-hidden="true">{icon}</span>
      </button>
    );
  };

  const toggleStatusOption = (value) => {
    setStatusFilters((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      }
      return [...prev, value];
    });
  };

  // Helper to validate Firestore doc IDs
  const isValidDocId = (id) => typeof id === 'string' && id.trim() !== '' && !id.includes('/');

  const togglePreview = (orderId) => {
    setPreviewToggles((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  useEffect(() => {
    if (resetSelectedOrder) {
      setActiveOrder(false);
    }
  }, [resetSelectedOrder]);

  useEffect(() => {
    if (!statusMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (!statusMenuRef.current) return;
      if (statusMenuRef.current.contains(event.target)) return;
      setStatusMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusMenuOpen]);

  const handleDownloadAll = async (order) => {
    const zip = new JSZip();
    const customerIdRaw = order.customerId;
    const customerId = String(customerIdRaw ?? '').trim();
    let userData = {};
    if (isValidDocId(customerId)) {
      try {
        const docRef = doc(db, 'users', customerId);
        const docSnap = await getDoc(docRef);
        userData = docSnap.exists() ? docSnap.data() : {};
      } catch (e) {
        console.warn('Failed to fetch user for customerId', customerId, e);
      }
    } else {
      console.warn('Skipping invalid customerId for order', order?.id, customerIdRaw);
    }
    const folderName = userData.freshbooksClientId?.organization || userData.lastName || "order";
    const folder = zip.folder(folderName);

    await Promise.all(
      order.images.map(async (img, i) => {
        const response = await fetch(img.url || img);
        const blob = await response.blob();
        folder.file(`image-${i + 1}.png`, blob);
      })
    );

    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `${folderName}.zip`);
  };

  useEffect(() => {
    const now = Date.now();
    localStorage.setItem("lastVisitedOrdersPage", now.toString());
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) return;
      const fetchUsers = async () => {
        const newUserDetails = { ...userDetails };
        // Normalize, validate, and de-duplicate customer IDs
        const uniqueIds = Array.from(
          new Set(
            orders
              .map((o) => o.customerId)
              .filter((id) => id !== null && id !== undefined)
              .map((id) => String(id).trim())
              .filter((id) => isValidDocId(id))
          )
        ).filter((id) => !newUserDetails[id]);

        if (!uniqueIds.length) return;

        const userFetches = uniqueIds.map(async (id) => {
          try {
            const docRef = doc(db, 'users', id);
            const docSnap = await getDoc(docRef);
            return { id, data: docSnap.exists() ? docSnap.data() : {} };
          } catch (err) {
            console.warn('User doc fetch failed for id', id, err);
            return { id, data: {} };
          }
        });

        const results = await Promise.all(userFetches);
        results.forEach(({ id, data }) => { newUserDetails[id] = data; });
        setUserDetails(newUserDetails);
      };
      if (orders.length > 0) fetchUsers();
    });
    return () => unsubscribe();
  }, [orders]);

  useEffect(() => {
    // Sort orders by createdAt descending before using them for initial statuses
    const sortedOrders = [...orders].sort((a, b) => {
      const aDate =
        a.createdAt?.toDate?.() ??
        (typeof a.createdAt === "string" ? new Date(a.createdAt) : null);
      const bDate =
        b.createdAt?.toDate?.() ??
        (typeof b.createdAt === "string" ? new Date(b.createdAt) : null);
      if (!aDate || !bDate) return 0;
      return bDate - aDate;
    });
    const initialStatuses = {};
    sortedOrders.forEach((order) => {
      initialStatuses[order.id] = order.status;
    });
    setOrderStatusUpdates(initialStatuses);
  }, [orders]);

  useEffect(() => {
    // Remove selections that are no longer present in the orders list
    const ids = new Set(orders.map(o => o.id));
    setSelectedOrders(prev => {
      const next = new Set();
      prev.forEach(id => { if (ids.has(id)) next.add(id); });
      return next;
    });
  }, [orders]);

  // Detect any pending orders older than 2 days
  const stalePendingOrders = orders.filter((o) => {
    const status = String(o.status || "").toLowerCase();
    if (status !== "pending") return false;
    const created = o.createdAt?.toDate?.() ?? new Date(o.createdAt);
    if (!(created instanceof Date) || isNaN(created)) return false;
    const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;
    return Date.now() - created.getTime() > TWO_DAYS_MS;
  });

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      setOrderStatusUpdates((prev) => ({
        ...prev,
        [orderId]: newStatus,
      }));

      const orderRef = doc(db, "orders", orderId);
      await updateDoc(orderRef, { status: newStatus });
      console.log(`✅ Order ${orderId} status updated to ${newStatus} in Firestore.`);
      if (typeof fetchOrders === 'function') {
        try {
          await fetchOrders();
        } catch (err) {
          console.error('Failed to refresh orders after status update:', err);
        }
      }
    } catch (error) {
      console.error(`❌ Failed to update order ${orderId} status:`, error);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    const confirm = window.confirm('Delete this order? This cannot be undone.');
    if (!confirm) return;
    try {
      await deleteDoc(doc(db, 'orders', orderId));
      setSelectedOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      if (activeOrder && activeOrder.id === orderId) {
        setActiveOrder(null);
      }
      if (typeof fetchOrders === 'function') {
        try {
          await fetchOrders();
        } catch (err) {
          console.error('Failed to refresh orders after delete:', err);
        }
      }
    } catch (err) {
      console.error('Failed to delete order:', err);
      alert('Failed to delete order. Check console for details.');
    }
  };

  useEffect(() => {
    if (typeof fetchOrders !== 'function') return;
    const intervalId = setInterval(() => {
      Promise.resolve(fetchOrders()).catch((err) => {
        console.error('Periodic order refresh failed:', err);
      });
    }, 60_000);
    return () => clearInterval(intervalId);
  }, [fetchOrders]);

  const getImageName = (img) => {
    if (!img) return "";
    if (typeof img === "string") {
      try {
        const noQuery = img.split("?")[0];
        const lastSeg = noQuery.split("/").pop() || "";
        return lastSeg.toLowerCase();
      } catch (_) {
        return String(img).toLowerCase();
      }
    }
    if (typeof img === "object") {
      if (img.name) return String(img.name).toLowerCase();
      if (img.url) {
        const noQuery = img.url.split("?")[0];
        const lastSeg = noQuery.split("/").pop() || "";
        return lastSeg.toLowerCase();
      }
    }
    return "";
  };

  const getMatchLabels = (order) => {
    const labels = [];
    const s = searchTerm.toLowerCase().trim();
    if (!s) return labels;

    const user = userDetails[order.customerId];
    const fullName = user
      ? `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase()
      : "";
    const companyName = user && user.freshbooksClientId?.organization
      ? String(user.freshbooksClientId.organization).toLowerCase()
      : "";
    const orderId = order.id?.toString().toLowerCase() ?? "";
    const hasName = fullName.includes(s) || companyName.includes(s);
    const hasOrderId = orderId.includes(s);
    const hasImageName = Array.isArray(order.pictures)
      ? order.pictures.some((img) => getImageName(img).includes(s))
      : false;

    const pushIf = (cond, text) => cond && labels.push(text);
    if (searchBy === "name") {
      pushIf(hasName, "Has matching name");
    } else if (searchBy === "orderId") {
      pushIf(hasOrderId, "Has matching order ID");
    } else if (searchBy === "imageName") {
      pushIf(hasImageName, "Has matching picture name");
    } else {
      pushIf(hasName, "Has matching name");
      pushIf(hasOrderId, "Has matching order ID");
      pushIf(hasImageName, "Has matching picture name");
    }

    return labels;
  };

  const getFilteredOrders = useCallback(() => {
    const filteredOrders = orders
      .filter((order) => {
        const search = searchTerm.toLowerCase().trim();
        const user = userDetails[order.customerId];
        const fullName = user ? `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase() : '';
        const companyName = user && user.freshbooksClientId?.organization ? String(user.freshbooksClientId.organization).toLowerCase() : '';
        const orderId = order.id?.toString().toLowerCase() ?? '';
        const matchesName = fullName.includes(search) || companyName.includes(search);
        const matchesOrderId = orderId.includes(search);
        const matchesImageName = Array.isArray(order.pictures) ? order.pictures.some((img) => {
          const str = typeof img === 'string' ? img : (img?.name || img?.url || '');
          return String(str).toLowerCase().includes(search);
        }) : false;
        const baseMatch = !search
          ? true
          : searchBy === 'name'
            ? matchesName
            : searchBy === 'orderId'
              ? matchesOrderId
              : matchesName || matchesOrderId || matchesImageName;

        const statusNormalized = normalizeStatus(order.status);
        const paymentState = getPaymentState(order);
        const tabMatch = (() => {
          switch (tab) {
            case 'Unfulfilled':
              return !['completed', 'cancelled'].includes(statusNormalized);
            case 'Unpaid':
              return paymentState !== 'paid';
            case 'Open':
              return ['pending', 'downloaded', 'inprogress'].includes(statusNormalized);
            case 'Closed':
              return ['completed', 'cancelled'].includes(statusNormalized);
            default:
              return true;
          }
        })();

        const statusValues = STATUS_OPTIONS.map((option) => option.value);
        const qualifierNormalized = normalizeStatus(searchQualifier);
        const statusFilterActive = searchBy === 'status' && statusValues.includes(qualifierNormalized);
        const matchesStatus = statusFilterActive ? statusNormalized === qualifierNormalized : true;
        const matchesSelectedStatuses = statusFilters.length
          ? statusFilters.includes(statusNormalized)
          : true;

        return baseMatch && tabMatch && matchesStatus && matchesSelectedStatuses;
      })
      .sort(compareOrders);
    return filteredOrders;
  }, [
    orders,
    userDetails,
    searchTerm,
    searchBy,
    searchQualifier,
    statusFilters,
    tab,
    tableSort,
  ]);

  const toggleSelectOrder = (orderId, checked) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (checked) next.add(orderId); else next.delete(orderId);
      return next;
    });
  };

  const selectAllFiltered = (checked) => {
    const filtered = getFilteredOrders();
    setSelectedOrders(prev => {
      if (!checked) return new Set();
      const next = new Set(prev);
      filtered.forEach(o => next.add(o.id));
      return next;
    });
  };

  useEffect(() => {
    if (typeof onVisibleOrdersChange !== 'function') return;
    const filtered = getFilteredOrders();
    const itemsPerPage = 20;
    const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
    const page = Math.min(currentPage, totalPages);
    const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    const ids = paginated.map((order) => order.id);
    onVisibleOrdersChange(ids);
  }, [getFilteredOrders, currentPage, onVisibleOrdersChange]);

  const handleDeleteSelected = async () => {
    setBulkMenuOpen(false);
    const ids = Array.from(selectedOrders);
    if (!ids.length) {
      alert('No orders selected.');
      return;
    }
    const ok = window.confirm(`Delete ${ids.length} selected order${ids.length > 1 ? 's' : ''}? This cannot be undone.`);
    if (!ok) return;
    try {
      await Promise.all(ids.map(id => deleteDoc(doc(db, 'orders', id))));
      setSelectedOrders(new Set());
      await fetchOrders?.();
      console.log(`🗑️ Deleted ${ids.length} order(s).`);
    } catch (err) {
      console.error('Bulk delete failed:', err);
      alert('Failed to delete some orders. Check console for details.');
    }
  };

  const handleSetInvoiceIdBulk = async () => {
    const ids = Array.from(selectedOrders);
    if (!ids.length) {
      alert('No orders selected.');
      setBulkMenuOpen(false);
      return;
    }

    const input = window.prompt(
      `Enter the FreshBooks invoice URL or ID to apply to ${ids.length} order${ids.length > 1 ? 's' : ''}:`
    );
    if (input === null) {
      return;
    }

    const invoiceId = extractInvoiceIdFromInput(input);
    if (!invoiceId) {
      alert('Could not determine a valid invoice ID from that input.');
      return;
    }

    try {
      await Promise.all(
        ids.map((id) =>
          updateDoc(doc(db, 'orders', id), {
            invoiceId,
            'invoice.invoiceId': invoiceId,
          })
        )
      );
      setBulkMenuOpen(false);
      alert(`Invoice ID set to ${invoiceId} for ${ids.length} order${ids.length > 1 ? 's' : ''}.`);
      await fetchOrders?.();
    } catch (err) {
      console.error('Failed to update invoice IDs:', err);
      alert('Failed to update some orders. Check console for details.');
    }
  };

  useEffect(() => {
    if (!bulkMenuOpen) return;
    const onClick = (e) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) {
        setBulkMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [bulkMenuOpen]);

  useEffect(() => {
    if (!selectedOrders.size) {
      setBulkMenuOpen(false);
    }
  }, [selectedOrders]);

  return (
    <div className="op-page">
      <div className="op-shell">
        <header className="op-header">
          <h1>Orders</h1>
          <div className="op-header__right">
            <button className="op-btn">Export</button>
            <div className="od-menu" ref={bulkMenuRef}>
              <button
                className="op-btn"
                onClick={() => setBulkMenuOpen((prev) => !prev)}
                disabled={selectedOrders.size === 0}
              >
                More actions ▾{selectedOrders.size ? ` (${selectedOrders.size})` : ''}
              </button>
              {bulkMenuOpen && (
                <div className="od-menu__list">
                  <button
                    className="od-menu__item"
                    onClick={handleSetInvoiceIdBulk}
                  >
                    Set invoice ID
                  </button>
                  <button
                    className="od-menu__item"
                    onClick={handleDeleteSelected}
                  >
                    Delete selected
                  </button>
                </div>
              )}
            </div>
            <button className="op-btn op-btn--primary">Create order</button>
          </div>
        </header>

        <div className="op-tabs">
          {['All', 'Unfulfilled', 'Unpaid', 'Open', 'Closed'].map((t) => (
            <button key={t} className={`op-tab ${tab === t ? 'is-active' : ''}`} onClick={() => setTab(t)}>{t}</button>
          ))}
          <div className={`op-filterPicker${statusFilters.length ? ' has-selection' : ''}`} ref={statusMenuRef}>
            <button
              type="button"
              className={`op-tab op-tab--add ${statusMenuOpen ? 'is-active' : ''}`}
              onClick={() => setStatusMenuOpen((prev) => !prev)}
            >
              + Status{statusFilters.length ? ` (${statusFilters.length})` : ''}
            </button>
            {statusFilters.length > 0 && (
              <div className="op-filterPicker__chips">
                {statusFilters.map((value) => {
                  const match = STATUS_OPTIONS.find((option) => option.value === value);
                  const label = match?.label || value;
                  return (
                    <span key={value} className="op-chip">
                      {label}
                      <button
                        type="button"
                        className="op-chip__close"
                        aria-label={`Remove ${label} filter`}
                        onClick={() => toggleStatusOption(value)}
                      >
                        x
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            {statusMenuOpen && (
              <div className="op-filterPicker__menu">
                {STATUS_OPTIONS.map((option) => {
                  const checked = statusFilters.includes(option.value);
                  return (
                    <label key={option.value} className="op-filterPicker__option">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleStatusOption(option.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
                <button
                  type="button"
                  className="op-filterPicker__clear"
                  onClick={() => setStatusFilters([])}
                  disabled={statusFilters.length === 0}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="op-toolbar">
          <div className="op-toolbar__left">
            <button onClick={fetchOrders} className="op-btn op-btn--ghost">Refresh</button>
            <div className="op-search">
              <svg viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 5 1.5-1.5-5-5zM9.5 14C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" /></svg>
              <input
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </div>
          </div>
          <div className="op-toolbar__right">
            <select
              value={searchBy}
              onChange={(e) => { const val = e.target.value; setSearchBy(val); setSearchQualifier(val === 'status' ? 'pending' : 'asc'); }}
            >
              <option value="">Filter by...</option>
              <option value="orderId">Order ID</option>
              <option value="name">Name</option>
              <option value="status">Status</option>
            </select>
            {searchBy && searchBy !== 'name' && (
              <select className="second-filter" value={searchQualifier} onChange={(e) => setSearchQualifier(e.target.value)}>
                {searchBy === 'status' ? (
                  <>
                    <option value="pending">Pending</option>
                    <option value="downloaded">Downloaded</option>
                    <option value="inprogress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </>
                ) : (
                  <>
                    <option value="asc">Ascending</option>
                    <option value="desc">Descending</option>
                  </>
                )}
              </select>
            )}
          </div>
        </div>

        {/* content area: list OR details */}
        {!activeOrder ? (
          (() => {
            const filteredOrders = getFilteredOrders();
            const itemsPerPage = 20;
            const totalPages = Math.ceil(filteredOrders.length / itemsPerPage) || 1;
            const page = Math.min(currentPage, totalPages);
            const paginated = filteredOrders.slice((page - 1) * itemsPerPage, page * itemsPerPage);

            const allFilteredSelected = filteredOrders.length > 0 && filteredOrders.every(o => selectedOrders.has(o.id));

            return (
              <div className="op-table">
                <div className="op-head">
                  <div className="op-cell op-cell--check">
                    <input
                      type="checkbox"
                      aria-label="select all"
                      checked={allFilteredSelected}
                      onChange={(e) => selectAllFiltered(e.target.checked)}
                    />
                  </div>
                  <div className="op-cell op-cell--order">{renderSortableHeader('order', 'Order')}</div>
                  <div className="op-cell op-cell--date">{renderSortableHeader('date', 'Date')}</div>
                  <div className="op-cell op-cell--customer">{renderSortableHeader('customer', 'Customer')}</div>
                  <div className="op-cell op-cell--total">{renderSortableHeader('total', 'Total')}</div>
                  <div className="op-cell op-cell--items">{renderSortableHeader('items', 'Items')}</div>
                  <div className="op-cell op-cell--fulfillment">{renderSortableHeader('fulfillment', 'Fulfilment')}</div>
                  <div className="op-cell op-cell--payment">{renderSortableHeader('payment', 'Payment')}</div>
                  <div className="op-cell op-cell--action">Action</div>
                </div>

                {paginated.map((order) => {
                  const user = userDetails[order.customerId];
                  const customerName =
                    user?.freshbooksClientId?.organization || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Unnamed Customer';
                  const createdAt = order.createdAt?.toDate?.() || new Date(order.createdAt);
                  const selected = selectedOrders.has(order.id);
                  return (
                    <OrderCard
                      key={order.id}
                      order={{
                        ...order, customerName, createdAt,
                        avatarUrl: (user && (user.profileImageURL || user.profileImageUrl || user.photoURL)) || null,
                      }}
                      selected={selected}
                      onSelectChange={(checked) => toggleSelectOrder(order.id, checked)}
                      onClick={() => setActiveOrder({ ...order, customerName, customerData: userDetails[order.customerId] })}
                      onDelete={() => handleDeleteOrder(order.id)}
                    />
                  );
                })}

                <div className="op-pagination">
                  {buildPaginationItems(page, totalPages).map((item) => {
                    if (typeof item !== "number") {
                      return (
                        <span key={item} className="op-pagination__ellipsis">
                          ...
                        </span>
                      );
                    }
                    return (
                      <button
                        key={item}
                        className={page === item ? "active" : ""}
                        onClick={() => setCurrentPage(item)}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()
        ) : (
          userDetails[activeOrder.customerId] ? (
            <div className="order-details-container">
              <OrderDetails
                selectedOrder={activeOrder}
                onBack={() => setActiveOrder(null)}
                handleStatusChange={handleStatusChange}
                currentStatus={orderStatusUpdates[activeOrder.id]}
                customerData={userDetails[activeOrder.customerId]}
              />
            </div>
          ) : (
            <div className="loading-container">Loading...</div>
          )
        )}
      </div>
    </div>
  );
}

export default OrdersPanel;
